import { useState, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import { FaLinkedinIn } from "react-icons/fa";

/* ================= ICONS ================= */

// Frontend
import IconReact from "../assets/React-icon.svg.png";
import IconJS from "../assets/javascript-icon-png-23.png";
import IconHtml from "../assets/HTML5_Badge.svg.png";
import IconCSS from "../assets/css.svg";
import IconNat from "../assets/React_Logo_SVG.svg.png";
import IconTyp from "../assets/Typescript.svg.png";
import IconExpo from "../assets/expo.webp";
import IconNext from "../assets/next.jpg";
import IconTail from "../assets/tailwind.png";
import IconFram from "../assets/framermotion.webp";

// Backend
import IconNode from "../assets/1200px-Node.js_logo.svg.png";
import IconMDB from "../assets/mongodb-icon.svg";
import IconExp from "../assets/expressjs_logo_icon_169185.png";
import IconPost from "../assets/postgreSQL.jpg";
import IconPyDjan from "../assets/python-django.png";
import IconJwt from "../assets/jwt.png";
import IconMan from "../assets/postman.webp";
import IconChroma from "../assets/chromadb.png";

// DevOps
import IconNet from "../assets/netlifyicon.png";
import IconGit from "../assets/git.png";
import IconLab from "../assets/gitlab.png";
import IconVercel from "../assets/vercel.png";
import IconGithub from "../assets/github.svg";
import IconDBea from "../assets/dbeaver.png";
import IconSrip from "../assets/stripe.svg";
import IconTan from "../assets/tanstack-query.png";
import IconPuppeteer from "../assets/puppeteer.png";
import IconLinux from "../assets/linux.png";

// UI / UX
import IconFigma from "../assets/figma-icon.png";
import IconMUI from "../assets/material-ui-icon-512x406-fhnu85xg.png";
import IconXD from "../assets/adobe-xd-icon.webp";
import IconCanva from "../assets/canva.webp";
import IconShadcn from "../assets/shadcnui.webp";
import IconLottie from "../assets/lottiefiles.webp";

/* ================= DATA ================= */

const skillsData = {
  Frontend: [
    { name: "React", icon: IconReact },
    { name: "JavaScript", icon: IconJS },
    { name: "HTML", icon: IconHtml },
    { name: "CSS", icon: IconCSS },
    { name: "React Native", icon: IconNat },
    { name: "TypeScript", icon: IconTyp },
    { name: "Expo", icon: IconExpo },
    { name: "Next.js", icon: IconNext },
    { name: "Tailwind CSS", icon: IconTail },
    { name: "Framer Motion", icon: IconFram },
  ],
  Backend: [
    { name: "NodeJS", icon: IconNode },
    { name: "MongoDB", icon: IconMDB },
    { name: "Express", icon: IconExp },
    { name: "PostgreSQL", icon: IconPost },
    { name: "Python Django", icon: IconPyDjan },
    { name: "JWT", icon: IconJwt },
    { name: "Postman", icon: IconMan },
    { name: "ChromaDB", icon: IconChroma },
  ],
  "Outils & DevOps": [
    { name: "Git", icon: IconGit },
    { name: "GitHub", icon: IconGithub },
    { name: "GitLab", icon: IconLab },
    { name: "Netlify", icon: IconNet },
    { name: "Vercel", icon: IconVercel },
    { name: "DBeaver", icon: IconDBea },
    { name: "Stripe", icon: IconSrip },
    { name: "TanStack Query", icon: IconTan },
    { name: "Puppeteer", icon: IconPuppeteer },
    { name: "Linux", icon: IconLinux },
  ],
  "UI/UX": [
    { name: "Figma", icon: IconFigma },
    { name: "Material UI", icon: IconMUI },
    { name: "Adobe XD", icon: IconXD },
    { name: "Canva", icon: IconCanva },
    { name: "Shadcn UI", icon: IconShadcn },
    { name: "LottieFiles", icon: IconLottie },
  ],
};

/* ================= COMPONENT ================= */

const SkillsSection = () => {
  const [selectedCategory, setSelectedCategory] = useState("Frontend");
  const controls = useAnimation();

  const startAnimation = () => {
    controls.start({
      x: ["0%", "-50%"],
      transition: {
        repeat: Infinity,
        duration: 20,
        ease: "linear",
      },
    });
  };

  const stopAnimation = () => {
    controls.stop();
  };

  useEffect(() => {
    startAnimation();
  }, [selectedCategory]);

  return (
    <section className="section services-section" id="services">
      <div className="container mx-auto">

        {/* TITLE */}
        <h2 className="h2 text-accent mb-6 flex items-center gap-4">
          Mes Compétences
          <a
            href="https://www.linkedin.com/in/priscilla-h-b784b1b8/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600"
          >
            <FaLinkedinIn size={24} />
          </a>
        </h2>

        {/* FILTERS */}
        <div className="flex gap-4 mb-16 flex-wrap">
          {Object.keys(skillsData).map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-lg font-semibold transition ${
                selectedCategory === cat
                  ? "bg-accent text-white"
                  : "bg-gray-700 text-gray-200 hover:bg-accent hover:text-white"
              }`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* CARROUSEL */}
        <div
          className="
            relative mx-auto w-[90%] lg:w-[70%]
            overflow-hidden
            [mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]
            [-webkit-mask-image:linear-gradient(to_right,transparent,black_10%,black_90%,transparent)]
          "
          onMouseEnter={stopAnimation}
          onMouseLeave={startAnimation}
        >
          <motion.div className="flex gap-6 w-max" animate={controls}>
            {[...skillsData[selectedCategory], ...skillsData[selectedCategory]].map(
              (skill, index) => (
                <motion.div
                  key={index}
                  whileHover={{
                  borderColor: "rgba(66, 166, 227, 0.8)",
                    boxShadow: `
                      0 0 10px rgba(66, 166, 227, 0.5),
                      0 0 20px rgba(179, 54, 238, 0.4),
                      0 0 30px rgba(255, 86, 246, 0.3)`,
                  }}
                  transition={{ duration: 0.35 }}
                  className="
                    flex items-center gap-4 px-6 py-4
                    bg-white/10 backdrop-blur-sm
                    rounded-xl
                    border border-transparent
                    cursor-pointer
                  "
                >
                  <img
                    src={skill.icon}
                    alt={skill.name}
                    className="w-16 h-16 object-contain"
                  />
                  <span className="text-white font-semibold text-sm whitespace-nowrap">
                    {skill.name}
                  </span>
                </motion.div>
              )
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;